import {
  ArrowDownward,
  ArrowUpward,
  Folder as FolderIcon,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useRef, type ReactNode } from 'react';
import { formatFileSize, type FileSortKey } from '../../utils/fileStorageHelpers';
import type { FileStorageItem, FileStorageListResponse } from '../../api/fileStorageApi';

type FileStorageItemsTableProps = {
  folderData?: FileStorageListResponse;
  sortedItems: FileStorageItem[];
  showProgress: boolean;
  sortKey: FileSortKey;
  sortDirection: 'asc' | 'desc';
  itemIcon: (item: FileStorageItem) => ReactNode;
  selectedFileId: string | null;
  onSort: (key: FileSortKey) => void;
  onOpenParent: () => void;
  onOpenItem: (item: FileStorageItem) => void | Promise<void>;
  onToggleFavorite: (item: FileStorageItem) => void | Promise<void>;
};

const SortIndicator = ({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) => {
  if (!active) return null;
  return direction === 'asc' ? (
    <ArrowUpward fontSize="inherit" />
  ) : (
    <ArrowDownward fontSize="inherit" />
  );
};

export const FileStorageItemsTable = ({
  folderData,
  sortedItems,
  showProgress,
  sortKey,
  sortDirection,
  itemIcon,
  selectedFileId,
  onSort,
  onOpenParent,
  onOpenItem,
  onToggleFavorite,
}: FileStorageItemsTableProps) => {
  const selectedRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!selectedFileId || !selectedRowRef.current) return;
    selectedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedFileId, sortedItems]);

  return (
    <Paper variant="outlined" sx={{ mt: 1.5, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell onClick={() => onSort('name')} sx={{ cursor: 'pointer' }}>
              Name <SortIndicator active={sortKey === 'name'} direction={sortDirection} />
            </TableCell>
            <TableCell onClick={() => onSort('updatedAt')} sx={{ cursor: 'pointer' }}>
              Updated <SortIndicator active={sortKey === 'updatedAt'} direction={sortDirection} />
            </TableCell>
            <TableCell onClick={() => onSort('length')} sx={{ cursor: 'pointer' }}>
              Size <SortIndicator active={sortKey === 'length'} direction={sortDirection} />
            </TableCell>
            <TableCell width={40} />
          </TableRow>
        </TableHead>
        <TableBody>
          {folderData?.haveParentId && (
            <TableRow hover onClick={onOpenParent} sx={{ cursor: 'pointer' }}>
              <TableCell>
                <FolderIcon fontSize="small" color="warning" />
              </TableCell>
              <TableCell sx={{ color: 'primary.main' }}>...</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          )}
          {sortedItems.length === 0 && !showProgress ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No files in current folder
              </TableCell>
            </TableRow>
          ) : (
            sortedItems.map((item) => (
              <TableRow
                key={item.id}
                ref={!item.isFolder && selectedFileId === item.id ? selectedRowRef : undefined}
                hover
                selected={!item.isFolder && selectedFileId === item.id}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  void onOpenItem(item);
                }}
              >
                <TableCell>{itemIcon(item)}</TableCell>
                <TableCell sx={{ cursor: 'pointer', color: 'primary.main' }}>{item.name}</TableCell>
                <TableCell>
                  {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : ''}
                </TableCell>
                <TableCell>{item.isUrl ? '' : formatFileSize(item)}</TableCell>
                <TableCell>
                  {!item.isFolder && (
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        void onToggleFavorite(item);
                      }}
                    >
                      {item.isFavorite ? (
                        <Star fontSize="small" color="warning" />
                      ) : (
                        <StarBorder fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {showProgress && (
        <Stack direction="row" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Stack>
      )}
    </Paper>
  );
};
